// The analysis-recommendation transport (contract 03 §6): two GETs, no state, no i18n.
//
// A class of its own rather than two more methods on `CreativeAiApi`, which sits at 389 of the
// 400-line budget — the same reason `CreativeLabelEditApi` is separate. The local `requestConfig` /
// `base` / `featureRequest` helpers mirror that sibling verbatim.
import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import { decodeRecommendationExecution } from '../models/recommendation.codec'
import type { RecommendationExecution } from '../models/recommendation.types'

import type { CreativeAiScope } from './CreativeAiApi'

import { ApiError, appHttp, decodeDto, unwrap } from '@/shared/http'

function requestConfig(signal?: AbortSignal): AxiosRequestConfig {
  return {
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

function base(scope: CreativeAiScope): string {
  return `/accounts/${encodeURIComponent(String(scope.accountId))}/workspaces/${encodeURIComponent(
    String(scope.workspaceId),
  )}/metadata/creative-ai/analysis-recommendations`
}

async function featureRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  } catch (cause) {
    throw ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }
}

export class RecommendationApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  /** The newest recommendation run for the workspace — what the landing strip renders. */
  async getLatest(scope: CreativeAiScope, signal?: AbortSignal): Promise<RecommendationExecution> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/latest/`, requestConfig(signal)),
    )
    return decodeDto(unwrap<unknown>(response), decodeRecommendationExecution)
  }

  /** One named run — the `?reco_execution=` deep link, which may point at an older execution. */
  async getExecution(
    scope: CreativeAiScope,
    executionId: string,
    signal?: AbortSignal,
  ): Promise<RecommendationExecution> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/${encodeURIComponent(executionId)}/`, requestConfig(signal)),
    )
    return decodeDto(unwrap<unknown>(response), decodeRecommendationExecution)
  }
}

export const recommendationApi = new RecommendationApi()
